import React, { useEffect, useState } from 'react';
import { type Banner } from 'mini-app-module-store/lib/store/schema/Banner';
import { format } from 'owa-strings/utilities/format';
import { getShownBanner } from 'mini-app-module-store/lib/selectors/getShownBanner';
import { hasQueryStringParameter } from 'owa-querystring';
import { LazyA2HSCustomBanner } from 'mini-a2hs';
import { lazyCanShowWebPushOnboarding } from 'mini-webpush-notifications';
import { LazyMiniPushNotificationsSurface } from 'mini-mail-module-campaign';
import { type MailRoutes } from 'mini-routes';
import { observer } from 'mobx-react-lite';
import { type PlacementId } from 'mini-campaign-bootstrap/lib/schema/PlacementId';
import { setShownBanner } from 'mini-app-module-store/lib/actions/setShownBanner';
import { useMatch } from 'mini-react-router-dom';
import {
    ARC_API_CALL_COMPLETED,
    campaignEventEmitter,
} from 'mini-campaign/lib/utils/campaignEventEmitters';

interface MailModuleBannerProps {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    elementToFocusOnClose?: React.RefObject<any>;
}

// This promise helps in deciding that Push notifications surface wont get mounted more than once in a session
let isPushNotificationsCampaignReceivedFromIrisPromise: Promise<boolean>;

/**
 * Functional component to render Mail Module Onboarding Banners which are mutually exclusive
 */
const MailModuleBannerBase = (props: MailModuleBannerProps) => {
    const [eligibleBanner, setEligibleBanner] = useState('None');

    useEffect(() => {
        (async () => {
            const shownBanner = getShownBanner();

            // Return if a campaign other than A2HS was shown.
            if (!['None', 'A2HS'].includes(shownBanner)) {
                return;
            }

            const canShowWebPushOnboarding = hasQueryStringParameter('mock-campaigns')
                ? true
                : await lazyCanShowWebPushOnboarding.importAndExecute();

            if (!canShowWebPushOnboarding) {
                setEligibleBanner('A2HS');

                // set shown banner centrally
                setShownBanner('A2HS');
                return;
            }

            /**
             * We are basically using one IRIS api call for a session
             */
            if (!isPushNotificationsCampaignReceivedFromIrisPromise) {
                /**
                 * We need to mount surface to know whether push notifications campaign will be shown
                 * This information will be derived from IRIS Api call response
                 */
                setEligibleBanner('PushNotifications');

                isPushNotificationsCampaignReceivedFromIrisPromise = new Promise<boolean>(resolve =>
                    campaignEventEmitter.on(format(ARC_API_CALL_COMPLETED, '88000645'), ev => {
                        // set shown banner centrally
                        if (ev.isCampaignActive) {
                            setShownBanner('PushNotifications');
                        }
                        resolve(ev.isCampaignActive);
                    })
                );
            }

            const isPushNotificationsOnboardingShown =
                await isPushNotificationsCampaignReceivedFromIrisPromise;

            // Never show A2HS Banner if Push notifications campaign is valid for a session
            if (!isPushNotificationsOnboardingShown) {
                setEligibleBanner('A2HS');

                // set shown banner centrally
                setShownBanner('A2HS');
            }
        })();
    }, []);

    let canShowA2HSBanner = !!useMatch({ path: '/:folderId', end: true });

    //Currently blocking A2HS banner, to ensure that push towards Native apps is not impacted.
    //This will later be restarted with different conditions.
    canShowA2HSBanner = false;

    switch (eligibleBanner) {
        case 'PushNotifications':
            return (
                <LazyMiniPushNotificationsSurface
                    elementToFocusOnClose={props.elementToFocusOnClose}
                />
            );
        case 'A2HS':
            return canShowA2HSBanner ? <LazyA2HSCustomBanner /> : null;
        default:
            return null;
    }
};

export const MailModuleBanner = observer(MailModuleBannerBase);
